import { Grid } from '@mui/material'
import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { IAssets } from '@sparling/financial-common'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { IDataItem, TableComponent } from '../../../../components/Table'
import { columns } from './pairsList'
import { assetsLoading, getAssetsData, createPairModal, updatePairModal } from '../../../../store/selectors'
import {
  getAssetsRequest,
  getListOfClientTypesRequest,
  openCreatePairModal,
  openUpdatePairModal,
} from '../../../../store/actions'
import { PairsFilters } from './PairsFilters'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { PairsCreateModal } from './PairsModals/PairsCreateModal'
import { PairsUpdateModal } from './PairsModals/PairsUpdateModal'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0),
    '& p': {
      display: 'none',
    },
  },
  filters: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    minWidth: 400,
  },
}))

export const Pairs: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()

  const { open: isOpenCreatePairModal } = useSelector(createPairModal)
  const { open: isOpenUpdatePairModal } = useSelector(updatePairModal)
  const assetsIsLoading = useSelector(assetsLoading)
  const { items, meta } = useSelector(getAssetsData)

  const existingInstruments = useMemo(() => items.map((i) => i.label as string), [items])

  const [editablePair, setEditablePair] = useState<IAssets | null>(null)
  const [checkedAssets, setCheckedAssets] = useState<Record<number, number[]>>({})

  const disableAllAction = useMemo(() => !Object.values(checkedAssets).some((ids) => ids.length > 0), [checkedAssets])
  const assetsArray = useMemo(() => Object.values(checkedAssets).flat(), [checkedAssets])

  const checkAllIdHandle = useCallback(
    (page: number) => {
      setCheckedAssets((prev) => {
        const allIds = items.map((item) => item.id)
        const isAllChecked = prev[page]?.length === allIds.length

        return {
          ...prev,
          [page]: isAllChecked ? [] : allIds,
        }
      })
    },
    [items],
  )

  const checkIdHandle = useCallback((event: MouseEvent<HTMLButtonElement>, page: number) => {
    const { id } = event.currentTarget.dataset
    if (!id) return
    const assetId = +id
    setCheckedAssets((prev) => {
      const pageIds = prev[page] || []
      const isChecked = pageIds.includes(assetId)
      return {
        ...prev,
        [page]: isChecked ? pageIds.filter((p) => p !== assetId) : [...pageIds, assetId],
      }
    })
  }, [])

  const openCreateModalHandle = useCallback(() => {
    dispatch(openCreatePairModal({ open: true }))
  }, [dispatch])

  const closeCreateModalHandle = useCallback(() => {
    dispatch(openCreatePairModal({ open: false }))
  }, [dispatch])

  const openUpdateModalHandle = useCallback(
    (id: number | string) => {
      if (!id || typeof id !== 'number') return
      setEditablePair(items.find((item) => item.id === id) || null)
      dispatch(openUpdatePairModal({ open: true, ids: [id] }))
    },
    [dispatch, items],
  )

  const closeUpdateModalHandle = useCallback(() => {
    dispatch(openUpdatePairModal({ open: false, ids: [] }))
    setEditablePair(null)
  }, [dispatch])

  const openUpdateModalForSelectedHandle = useCallback(() => {
    dispatch(openUpdatePairModal({ open: true, ids: assetsArray }))
  }, [dispatch, assetsArray])

  const loadAssetsData = useCallback(
    (page = 1) => {
      dispatch(getAssetsRequest({ page }))
    },
    [dispatch],
  )

  const loadListOfClientTypes = useCallback(() => {
    dispatch(getListOfClientTypesRequest())
  }, [dispatch])

  useEffect(() => {
    loadAssetsData()
    loadListOfClientTypes()
  }, [loadAssetsData, loadListOfClientTypes])

  useEffect(() => {
    history.push(`?page=1`)
  }, [history])

  return (
    <Grid container>
      <Grid container className={classes.topWrap}>
        <Grid container className={classes.filters}>
          <Grid item xs={10} display={'flex'} alignItems={'center'}>
            <PairsFilters />
          </Grid>
          <Grid item xs={2} display="flex" justifyContent={'flex-end'}>
            <GreenButton width={120} ml={8} text={'Create'} onClick={openCreateModalHandle} />
            <GreenButton
              width={120}
              ml={8}
              text={'Assign'}
              disabled={disableAllAction}
              onClick={openUpdateModalForSelectedHandle}
            />
          </Grid>
        </Grid>
      </Grid>
      <TableComponent
        columns={columns}
        data={items as IDataItem<IAssets>[]}
        params={meta}
        onCheck={checkIdHandle}
        onCheckAll={checkAllIdHandle}
        checkboxIds={checkedAssets}
        loadData={loadAssetsData}
        isShowEditIcon={true}
        isShowInfoIcon={false}
        onEdit={openUpdateModalHandle}
        loading={assetsIsLoading}
      />
      <PairsCreateModal
        isOpen={isOpenCreatePairModal}
        modalClose={closeCreateModalHandle}
        existingInstruments={existingInstruments}
      />
      <PairsUpdateModal
        isOpen={isOpenUpdatePairModal}
        modalClose={closeUpdateModalHandle}
        pair={editablePair}
        clearCheckedIds={() => setCheckedAssets({})}
      />
    </Grid>
  )
}
